import { lt } from './localized'
import type {
  VehicleDef,
  VehicleInstance,
  VehicleSize,
} from './types'

export type VehicleEffectiveStats = {
  lootMultiplier: number
  passengerSeats: number
  /**
   * Factor applied to trip duration.
   * - 1 = baseline
   * - <1 = shorter trips
   */
  durationMultiplier: number
  /**
   * Multiplier applied to durability loss per trip.
   * - 1 = baseline
   * - <1 = less wear
   */
  durabilityLossMultiplier: number
}

/** Slot cost per vehicle size inside the garage */
export const GARAGE_SLOT_COST: Record<VehicleSize, number> = {
  small: 1,
  medium: 2,
  large: 4,
}

/** Total small-equivalent slots per garage level */
export const GARAGE_LEVEL_CAPACITY: Record<number, number> = {
  1: 2, // level 1 = bike rack (2 small slots)
  2: 4, // level 2 = extended bay (4 small slots — fits cargo bikes, sedan, or 1 van)
  3: 7, // level 3 = depot yard — room for a van plus everyday bikes
}

export const VEHICLE_DEFS: Record<string, VehicleDef> = {
  'bike-city': {
    id: 'bike-city',
    name: lt('Stadsfiets', 'City bike'),
    blurb: lt(
      'Een robuuste stadsfiets met bagagerek. Snel genoeg om schroot te sjouwen.',
      'A sturdy city bike with a cargo rack. Fast enough to haul scrap.',
    ),
    size: 'small',
    lootMultiplier: 1.1,
    passengerSeats: 1,
    audioKind: 'bike',
    glyph: '🚲',
    parts: [
      {
        id: 'saddle',
        name: lt('Zadelrek', 'Saddle rack'),
        blurb: lt(
          'Stabiele bevestiging — minder wiebelen, meer efficiënt laden.',
          'Stable mountings — less wobble, more efficient loading.',
        ),
        glyph: '🪑',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 2 },
              { defId: 'scrap-plank', quantity: 2 },
            ],
            effects: { lootMultiplierFactor: 1.05 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 4 },
              { defId: 'scrap-electronics', quantity: 1 },
            ],
            effects: { lootMultiplierFactor: 1.08 },
          },
        },
      },
      {
        id: 'wheels',
        name: lt('Wielen', 'Wheels'),
        blurb: lt(
          'Nieuwe lagers en banden — minder slijtage op slechte wegen.',
          'Fresh bearings and tires — less wear on bad roads.',
        ),
        glyph: '🛞',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 3 },
              { defId: 'scrap-wire', quantity: 2 },
            ],
            effects: {
              durationMultiplierFactor: 0.97,
              durabilityLossMultiplierFactor: 0.98,
            },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 5 },
              { defId: 'scrap-electronics', quantity: 1 },
            ],
            effects: {
              durationMultiplierFactor: 0.95,
              durabilityLossMultiplierFactor: 0.97,
            },
          },
        },
      },
      {
        id: 'steer',
        name: lt('Stuurstangen', 'Steering'),
        blurb: lt(
          'Fijne controle — stuur rustiger, rij strakker, pluk meer uit elke rit.',
          'Fine control — steer smoother, ride tighter, extract more per run.',
        ),
        glyph: '🧭',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 2 },
              { defId: 'scrap-toolkit', quantity: 1 },
            ],
            effects: { lootMultiplierFactor: 1.02, durationMultiplierFactor: 0.99 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 4 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: {
              lootMultiplierFactor: 1.05,
              durationMultiplierFactor: 0.98,
              durabilityLossMultiplierFactor: 0.98,
            },
          },
        },
      },
    ],
  },
  'bike-mountain': {
    id: 'bike-mountain',
    name: lt('Mountainbike', 'Mountain bike'),
    blurb: lt(
      'Dikke banden, veersysteem. Rijdt over alles wat geen asfalt is.',
      'Fat tyres, full suspension. Handles anything that is not tarmac.',
    ),
    size: 'small',
    lootMultiplier: 1.15,
    passengerSeats: 1,
    audioKind: 'bike',
    glyph: '🚵',
    parts: [
      {
        id: 'suspension',
        name: lt('Vering', 'Suspension'),
        blurb: lt(
          'Veert beter — ruw terrein wordt een rustige rit.',
          'Better rebound — rough terrain becomes a calmer ride.',
        ),
        glyph: '🦾',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 3 },
              { defId: 'scrap-wire', quantity: 2 },
            ],
            effects: { durabilityLossMultiplierFactor: 0.97, lootMultiplierFactor: 1.01 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 5 },
              { defId: 'scrap-electronics', quantity: 1 },
            ],
            effects: {
              durabilityLossMultiplierFactor: 0.95,
              lootMultiplierFactor: 1.03,
            },
          },
        },
      },
      {
        id: 'wheels',
        name: lt('Wielen', 'Wheels'),
        blurb: lt(
          'Grip en rol — sneller over grind, minder energieverlies.',
          'Grip and roll — faster over gravel, less wasted effort.',
        ),
        glyph: '🛞',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 3 },
              { defId: 'scrap-wire', quantity: 2 },
            ],
            effects: { durationMultiplierFactor: 0.97, durabilityLossMultiplierFactor: 0.98 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 6 },
              { defId: 'scrap-electronics', quantity: 1 },
            ],
            effects: {
              durationMultiplierFactor: 0.95,
              durabilityLossMultiplierFactor: 0.97,
            },
          },
        },
      },
      {
        id: 'steer',
        name: lt('Stuurinstelling', 'Steering'),
        blurb: lt(
          'Beter traject — minder slippende bochten, meer opbrengst per tocht.',
          'Better line — fewer slipping turns, more yield per run.',
        ),
        glyph: '🧭',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 2 },
              { defId: 'scrap-toolkit', quantity: 1 },
            ],
            effects: { lootMultiplierFactor: 1.03, durationMultiplierFactor: 0.99 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 4 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: { lootMultiplierFactor: 1.06, durationMultiplierFactor: 0.98, durabilityLossMultiplierFactor: 0.98 },
          },
        },
      },
    ],
  },
  'car-sedan': {
    id: 'car-sedan',
    name: lt('Sedan — onbekend merk', 'Sedan — unknown make'),
    blurb: lt(
      'Oude benzinekar. Tank bijna vol. Kofferbak zit vol ruimte.',
      'Old petrol car. Tank near full. Plenty of boot space.',
    ),
    size: 'medium',
    lootMultiplier: 1.25,
    passengerSeats: 4,
    audioKind: 'car',
    glyph: '🚗',
    parts: [
      {
        id: 'engine',
        name: lt('Motor', 'Engine'),
        blurb: lt(
          'Stabieler verbrandingsproces — sneller aankomen met minder schade.',
          'More stable combustion — arrive sooner with less damage.',
        ),
        glyph: '🧱',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 6 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: { durationMultiplierFactor: 0.96, durabilityLossMultiplierFactor: 0.98 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 10 },
              { defId: 'scrap-electronics', quantity: 3 },
            ],
            effects: { durationMultiplierFactor: 0.93, durabilityLossMultiplierFactor: 0.95 },
          },
        },
      },
      {
        id: 'trunk',
        name: lt('Kofferbak', 'Trunk'),
        blurb: lt(
          'Bettered lading — minder verlies onderweg en meer laadruimte benut.',
          'Better loading — less loss in transit and more effective space.',
        ),
        glyph: '🎒',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 3 },
              { defId: 'scrap-plank', quantity: 4 },
            ],
            effects: { lootMultiplierFactor: 1.05, durabilityLossMultiplierFactor: 0.99 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 6 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: { lootMultiplierFactor: 1.1, durabilityLossMultiplierFactor: 0.98 },
          },
        },
      },
      {
        id: 'seats',
        name: lt('Stoelen', 'Seats'),
        blurb: lt(
          'Versterkte banken en riemen — extra plek als je het nodig hebt.',
          'Reinforced benches and straps — extra place when you need it.',
        ),
        glyph: '🪑',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 4 },
              { defId: 'scrap-plank', quantity: 3 },
            ],
            effects: { durationMultiplierFactor: 0.99, durabilityLossMultiplierFactor: 0.99 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 7 },
              { defId: 'scrap-toolkit', quantity: 1 },
              { defId: 'scrap-electronics', quantity: 1 },
            ],
            effects: {
              passengerSeatBonus: 1,
              durationMultiplierFactor: 0.98,
              durabilityLossMultiplierFactor: 0.98,
            },
          },
        },
      },
    ],
  },
  'bike-cargo': {
    id: 'bike-cargo',
    name: lt('Bakfiets', 'Cargo bike'),
    blurb: lt(
      'Met een houten bak voor. Laadt meer mee dan je verwacht.',
      'Wooden front box. Carries more than you expect.',
    ),
    size: 'medium',
    lootMultiplier: 1.2,
    passengerSeats: 1,
    audioKind: 'bike',
    glyph: '🚴',
    parts: [
      {
        id: 'box',
        name: lt('Bakwerk', 'Cargo box'),
        blurb: lt(
          'Harde plankvoering — minder gekraak en meer nuttige spullen per rit.',
          'Hard plank lining — less creak and more usable gear per run.',
        ),
        glyph: '📦',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 4 },
              { defId: 'scrap-plank', quantity: 4 },
            ],
            effects: { lootMultiplierFactor: 1.06 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 6 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: { lootMultiplierFactor: 1.1, durabilityLossMultiplierFactor: 0.99 },
          },
        },
      },
      {
        id: 'wheels',
        name: lt('Wielen', 'Wheels'),
        blurb: lt(
          'Rolweerstand omlaag — zwaarder beladen zonder dat het langzamer voelt.',
          'Lower rolling resistance — heavier loads without feeling slower.',
        ),
        glyph: '🛞',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 3 },
              { defId: 'scrap-wire', quantity: 2 },
            ],
            effects: { durationMultiplierFactor: 0.98, durabilityLossMultiplierFactor: 0.98 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 6 },
              { defId: 'scrap-wire', quantity: 3 },
            ],
            effects: { durationMultiplierFactor: 0.96, durabilityLossMultiplierFactor: 0.97 },
          },
        },
      },
      {
        id: 'gears',
        name: lt('Versnellingen', 'Gears'),
        blurb: lt(
          'Mechwerk met tanden — beter tempo op heuvels en bij wind.',
          'Toothed mechanical work — better pace on hills and in wind.',
        ),
        glyph: '⚙️',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 2 },
              { defId: 'scrap-toolkit', quantity: 1 },
              { defId: 'scrap-wire', quantity: 1 },
            ],
            effects: { durationMultiplierFactor: 0.99, lootMultiplierFactor: 1.02 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 5 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: { durationMultiplierFactor: 0.98, lootMultiplierFactor: 1.05, durabilityLossMultiplierFactor: 0.98 },
          },
        },
      },
    ],
  },
  'van-delivery': {
    id: 'van-delivery',
    name: lt('Bestelbus', 'Delivery van'),
    blurb: lt(
      'Half vol met pakketten die nooit zijn aangekomen. Nu gaan ze ergens heen.',
      'Half full of packages that never arrived. Now they go somewhere.',
    ),
    size: 'large',
    lootMultiplier: 1.4,
    passengerSeats: 6,
    audioKind: 'car',
    glyph: '🚐',
    parts: [
      {
        id: 'engine',
        name: lt('Motor', 'Engine'),
        blurb: lt(
          'Koelere run — minder uitval en minder schade per tocht.',
          'Cooler run — fewer failures and less damage per trip.',
        ),
        glyph: '🧱',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 8 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: { durationMultiplierFactor: 0.95, durabilityLossMultiplierFactor: 0.98 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 12 },
              { defId: 'scrap-electronics', quantity: 4 },
            ],
            effects: { durationMultiplierFactor: 0.92, durabilityLossMultiplierFactor: 0.95 },
          },
        },
      },
      {
        id: 'roofRack',
        name: lt('Dakklemmen', 'Roof rack'),
        blurb: lt(
          'Extra lading bovenop — minder leegtes en meer waarde per kilometer.',
          'Extra load on top — fewer empty moments, more value per mile.',
        ),
        glyph: '🧰',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 4 },
              { defId: 'scrap-plank', quantity: 3 },
            ],
            effects: { lootMultiplierFactor: 1.07 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 8 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: { lootMultiplierFactor: 1.13, durabilityLossMultiplierFactor: 0.99 },
          },
        },
      },
      {
        id: 'doors',
        name: lt('Deuren & banken', 'Doors & benches'),
        blurb: lt(
          'Slimme indeling — extra zitplek wanneer je team groter wordt.',
          'Smart layout — extra seat when your team grows.',
        ),
        glyph: '🚪',
        upgrades: {
          2: {
            toLevel: 2,
            costs: [
              { defId: 'scrap-rusty', quantity: 5 },
              { defId: 'scrap-toolkit', quantity: 1 },
            ],
            effects: { durabilityLossMultiplierFactor: 0.99 },
          },
          3: {
            toLevel: 3,
            costs: [
              { defId: 'scrap-rusty', quantity: 9 },
              { defId: 'scrap-electronics', quantity: 2 },
            ],
            effects: {
              passengerSeatBonus: 1,
              durationMultiplierFactor: 0.99,
              durabilityLossMultiplierFactor: 0.98,
            },
          },
        },
      },
    ],
  },
}

export const VEHICLE_DEF_LIST: VehicleDef[] = Object.values(VEHICLE_DEFS)

export function getVehicleDef(defId: string): VehicleDef | undefined {
  return VEHICLE_DEFS[defId]
}

function clampPartLevel(raw: unknown): 1 | 2 | 3 {
  const n = typeof raw === 'number' ? raw : 1
  const v = Math.floor(n)
  if (v <= 1) return 1
  if (v === 2) return 2
  return 3
}

export function getVehicleEffectiveStats(
  def: VehicleDef,
  instance: VehicleInstance,
): VehicleEffectiveStats {
  let lootMultiplier = def.lootMultiplier
  let passengerSeats = def.passengerSeats
  let durationMultiplier = 1
  let durabilityLossMultiplier = 1

  for (const part of def.parts) {
    const lvl = clampPartLevel(instance.partLevels?.[part.id])

    if (lvl >= 2) {
      const up2 = part.upgrades[2]
      if (up2) {
        lootMultiplier *= up2.effects.lootMultiplierFactor ?? 1
        passengerSeats += up2.effects.passengerSeatBonus ?? 0
        durationMultiplier *= up2.effects.durationMultiplierFactor ?? 1
        durabilityLossMultiplier *= up2.effects.durabilityLossMultiplierFactor ?? 1
      }
    }

    if (lvl >= 3) {
      const up3 = part.upgrades[3]
      if (up3) {
        lootMultiplier *= up3.effects.lootMultiplierFactor ?? 1
        passengerSeats += up3.effects.passengerSeatBonus ?? 0
        durationMultiplier *= up3.effects.durationMultiplierFactor ?? 1
        durabilityLossMultiplier *= up3.effects.durabilityLossMultiplierFactor ?? 1
      }
    }
  }

  // Seats should never go negative; clamp to a reasonable UI-friendly bound.
  passengerSeats = Math.max(0, passengerSeats)
  durationMultiplier = Math.max(0.5, Math.min(1.5, durationMultiplier))
  durabilityLossMultiplier = Math.max(0.5, Math.min(1.5, durabilityLossMultiplier))

  return {
    lootMultiplier,
    passengerSeats,
    durationMultiplier,
    durabilityLossMultiplier,
  }
}

export function getVehicleEffectiveStatsForInstance(
  instance: VehicleInstance,
): VehicleEffectiveStats {
  const def = getVehicleDef(instance.defId)
  if (!def) {
    return {
      lootMultiplier: 1,
      passengerSeats: 0,
      durationMultiplier: 1,
      durabilityLossMultiplier: 1,
    }
  }
  return getVehicleEffectiveStats(def, instance)
}

/** Scrap cost to repair 15% durability */
export const VEHICLE_REPAIR_SCRAP_COST = 2
export const VEHICLE_REPAIR_DURABILITY_PER_SCRAP = 15

/** Per-expedition durability loss range */
export const VEHICLE_DURABILITY_LOSS_MIN = 8
export const VEHICLE_DURABILITY_LOSS_MAX = 12

/** Super-rare random vehicle drop chances (after vehicle quest complete) */
export type VehicleDrop = {
  defId: string
  /** 0–1 probability per expedition */
  chance: number
}

export const RURAL_SOUTH_VEHICLE_DROPS: VehicleDrop[] = [
  { defId: 'bike-city', chance: 0.022 },
  { defId: 'bike-mountain', chance: 0.017 },
  { defId: 'bike-cargo', chance: 0.008 },
]

export const DEN_BOSCH_VEHICLE_DROPS: VehicleDrop[] = [
  { defId: 'bike-city', chance: 0.021 },
  { defId: 'bike-mountain', chance: 0.016 },
  { defId: 'bike-cargo', chance: 0.009 },
  { defId: 'car-sedan', chance: 0.009 },
  { defId: 'van-delivery', chance: 0.003 },
]

export function pickRandomVehicleDrop(
  drops: VehicleDrop[],
): string | null {
  const sum = drops.reduce((s, d) => s + d.chance, 0)
  if (sum <= 0) return null
  let r = Math.random()
  if (r >= sum) return null
  for (const drop of drops) {
    r -= drop.chance
    if (r < 0) return drop.defId
  }
  return drops[drops.length - 1]?.defId ?? null
}

/** Params for `vehicle_loot_pct` i18n (vehicle loot bonus vs baseline). */
export function vehicleLootYieldParams(multiplier: number): {
  sign: string
  pct: number
} {
  const raw = Math.round((multiplier - 1) * 100)
  return { sign: raw >= 0 ? '+' : '-', pct: Math.abs(raw) }
}
